import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CalendarCheck, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SalesActions } from "@/components/crm/SalesActions";
import { SalesShell } from "@/components/crm/SalesShell";
import {
  EmptyState,
  Money,
  RenewalBadge,
  RiskBadge,
  SelectField,
  Stat,
} from "@/components/crm/parts";
import { renewalSheets } from "@/lib/exports/crm";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import { renewalVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import {
  useAccounts,
  useContracts,
  useOpportunities,
  useQbrs,
  useSaveRecord,
  useStages,
} from "@/lib/crm/useCrm";
import {
  QBR_CADENCES,
  QBR_STATE_LABELS,
  RENEWAL_BAND_LABELS,
  RENEWAL_BAND_ORDER,
  bandOf,
  bandRows,
  cadenceMonths,
  hasRenewalOpportunity,
  headline,
  nextDueAfter,
  qbrState,
  riskWeight,
  type RenewalBandKey,
  liveContracts,
} from "@/lib/crm/renewals";
import { gbp, money, monthsUntil, type Contract, type Qbr } from "@/lib/crm/types";

export const Route = createFileRoute("/_authenticated/sales/renewals")({
  head: () => ({
    meta: [
      { title: "Renewals & QBRs — MyPriorities" },
      {
        name: "description",
        content:
          "Renewal warning bands, revenue at risk and quarterly business review cadences for every account.",
      },
      { property: "og:title", content: "Renewals & QBRs — MyPriorities" },
      {
        property: "og:description",
        content: "See what is up for renewal, what is exposed and which reviews are overdue.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: RenewalsPage,
});

function RenewalsPage() {
  return (
    <SalesShell
      section="customers"
      title="Renewals & QBRs"
      subtitle="What is up for renewal, what is exposed, and which reviews are due."
    >

      {({ orgId }) => <Renewals orgId={orgId} />}
    </SalesShell>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

function Renewals({ orgId }: { orgId: string }) {
  const contracts = useContracts(orgId);
  const accounts = useAccounts(orgId);
  const opportunities = useOpportunities(orgId);
  const stages = useStages(orgId);
  const qbrs = useQbrs(orgId);
  const { ownerLabel } = useOrgMembers(orgId);
  const saveOpportunity = useSaveRecord("crm_opportunities");
  const saveQbr = useSaveRecord("crm_qbrs");

  const [search, setSearch] = useState("");
  const [band, setBand] = useState<"all" | RenewalBandKey>("all");
  const [cover, setCover] = useState("all");

  const { scope } = useSalesOwner();
  const contractRows = scope(liveContracts(contracts.data ?? []));
  const accountRows = accounts.data ?? [];
  const oppRows = opportunities.data ?? [];
  const qbrRows = qbrs.data ?? [];

  const accountName = (id: string | null) =>
    accountRows.find((a) => a.id === id)?.name ?? "No account";

  const bands = useMemo(() => bandRows(contractRows, oppRows), [contractRows, oppRows]);
  const stats = useMemo(() => headline(contractRows, oppRows), [contractRows, oppRows]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contractRows
      .filter((c) => {
        if (band !== "all" && bandOf(c) !== band) return false;
        if (cover === "uncovered" && hasRenewalOpportunity(c, oppRows)) return false;
        if (cover === "covered" && !hasRenewalOpportunity(c, oppRows)) return false;
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) || accountName(c.account_id).toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.end_date ?? "9999").localeCompare(b.end_date ?? "9999"));
  }, [contractRows, oppRows, search, band, cover, accountRows]);

  const qbrByAccount = useMemo(() => {
    const map: Record<string, Qbr | undefined> = {};
    for (const q of qbrRows) map[q.account_id] = q;
    return map;
  }, [qbrRows]);

  async function createRenewal(contract: Contract) {
    const stage = (stages.data ?? [])[0];
    await saveOpportunity.mutateAsync({
      values: {
        organisation_id: orgId,
        account_id: contract.account_id,
        contract_id: contract.id,
        stage_id: stage?.id ?? null,
        name: `${contract.name} renewal`,
        opportunity_type: "renewal",
        product_name: contract.product_name,
        product_id: contract.product_id,
        expected_value: contract.total_value ?? contract.annual_value,
        expected_annual_value: contract.annual_value,
        expected_close_date: contract.end_date,
        forecast_category: "pipeline",
        next_action: "Book renewal conversation",
      },
    });
    toast.success("Renewal opportunity created");
  }

  async function saveCadence(accountId: string, cadence: string) {
    const existing = qbrByAccount[accountId];
    const next =
      cadenceMonths(cadence) && !existing?.next_due_on
        ? nextDueAfter(today(), cadence)
        : (existing?.next_due_on ?? null);
    await saveQbr.mutateAsync({
      id: existing?.id ?? null,
      values: existing
        ? { cadence, next_due_on: next }
        : { organisation_id: orgId, account_id: accountId, cadence, next_due_on: next },
    });
  }

  async function markHeld(accountId: string) {
    const existing = qbrByAccount[accountId];
    const cadence = existing?.cadence ?? "quarterly";
    const held = today();
    await saveQbr.mutateAsync({
      id: existing?.id ?? null,
      values: existing
        ? { last_held_on: held, next_due_on: nextDueAfter(held, cadence) }
        : {
            organisation_id: orgId,
            account_id: accountId,
            cadence,
            last_held_on: held,
            next_due_on: nextDueAfter(held, cadence),
          },
    });
    toast.success("QBR logged — next one scheduled");
  }

  async function setNextDue(accountId: string, date: string) {
    const existing = qbrByAccount[accountId];
    await saveQbr.mutateAsync({
      id: existing?.id ?? null,
      values: existing
        ? { next_due_on: date || null }
        : {
            organisation_id: orgId,
            account_id: accountId,
            cadence: "quarterly",
            next_due_on: date || null,
          },
    });
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Due in 12 months"
          value={gbp(stats.dueTwelveMonths)}
          hint="Annual value of contracts renewing"
        />
        <Stat label="Revenue at risk" value={gbp(stats.atRisk)} hint="Weighted by risk status" />
        <Stat
          label="Without a renewal deal"
          value={`${Math.round(stats.uncoveredShare * 100)}%`}
          hint="Of contracts renewing this year"
        />
        <Stat
          label="Average time to renewal"
          value={stats.averageMonths === null ? "—" : `${stats.averageMonths.toFixed(1)} months`}
        />
      </div>

      <section className="rounded-lg border border-border bg-card p-3">
        <h2 className="font-display text-sm font-semibold">Renewal bands</h2>
        <p className="text-xs text-muted-foreground">
          Click a band to filter the contract list below.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-3">Band</th>
                <th className="py-1.5 pr-3">Contracts</th>
                <th className="py-1.5 pr-3">Annual value</th>
                <th className="py-1.5 pr-3">At risk</th>
                <th className="py-1.5">No renewal deal</th>
              </tr>
            </thead>
            <tbody>
              {bands.map((row) => (
                <tr
                  key={row.key}
                  onClick={() => setBand(band === row.key ? "all" : row.key)}
                  className={
                    "cursor-pointer border-t border-border/60 hover:bg-accent/50 " +
                    (band === row.key ? "bg-accent/60" : "")
                  }
                >
                  <td className="py-1.5 pr-3 font-medium">{row.label}</td>
                  <td className="py-1.5 pr-3 tabular-nums">{row.count}</td>
                  <td className="py-1.5 pr-3">
                    <Money value={row.annual} />
                  </td>
                  <td className="py-1.5 pr-3">
                    <Money value={row.atRisk} />
                  </td>
                  <td className="py-1.5 tabular-nums">{row.uncovered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-full sm:basis-56">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contracts or accounts"
          />
        </div>
        <div className="min-w-0 basis-full sm:basis-48">
          <SelectField
            label="Band"
            value={band}
            onChange={(v) => setBand(v as "all" | RenewalBandKey)}
            options={[
              { value: "all", label: "All bands" },
              ...RENEWAL_BAND_ORDER.map((key) => ({ value: key, label: RENEWAL_BAND_LABELS[key] })),
            ]}
          />
        </div>
        <div className="min-w-0 basis-full sm:basis-48">
          <SelectField
            label="Renewal cover"
            value={cover}
            onChange={setCover}
            options={[
              { value: "all", label: "All contracts" },
              { value: "uncovered", label: "No renewal opportunity" },
              { value: "covered", label: "Renewal in pipeline" },
            ]}
          />
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-end gap-2 sm:w-auto sm:shrink-0">
          <SalesActions
            visual={() => renewalVisual(bands)}
            sourceNote="From your renewals dashboard"
            exports={[
              {
                label: "Renewals & QBRs",
                baseName: "renewals",
                sheets: () =>
                  renewalSheets({
                    contracts: rows,
                    accounts: accountRows,
                    opportunities: oppRows,
                    qbrByAccount,
                    ownerLabel,
                  }),
              },
            ]}
          />
        </div>
      </div>

      <section className="grid gap-2">
        {rows.length === 0 ? (
          <EmptyState
            title="No contracts in this view"
            body="Add contracts in the contract book, or widen the filters above."
          />
        ) : (
          rows.map((contract) => {
            const covered = hasRenewalOpportunity(contract, oppRows);
            const months = monthsUntil(contract.end_date);
            return (
              <article
                key={contract.id}
                className="rounded-lg border border-border bg-card p-3 md:flex md:items-center md:justify-between md:gap-4"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{contract.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {accountName(contract.account_id)}
                    {contract.product_name ? ` · ${contract.product_name}` : ""}
                    {months === null ? "" : ` · ${months} months to renewal`}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <RenewalBadge endDate={contract.end_date} />
                    <RiskBadge status={contract.risk_status} />
                    <Badge variant="outline">
                      <Money value={money(contract.annual_value)} currency={contract.currency} />{" "}
                      per year
                    </Badge>
                    <Badge variant="outline">
                      At risk{" "}
                      <Money
                        value={money(contract.annual_value) * riskWeight(contract.risk_status)}
                        currency={contract.currency}
                      />
                    </Badge>
                    {covered ? (
                      <Badge variant="secondary">
                        <CheckCircle2 className="size-3" /> Renewal in pipeline
                      </Badge>
                    ) : null}
                  </div>
                </div>
                <div className="mt-2 flex shrink-0 gap-2 md:mt-0">
                  {covered ? null : (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={saveOpportunity.isPending}
                      onClick={() => void createRenewal(contract)}
                    >
                      <Plus className="size-4" /> Create renewal deal
                    </Button>
                  )}
                </div>
              </article>
            );
          })
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-3">
        <h2 className="font-display text-sm font-semibold">Business review cadence</h2>
        <p className="text-xs text-muted-foreground">
          Set a rhythm per account. Logging a review rolls the next date forward automatically.
        </p>
        <div className="mt-3 grid gap-2">
          {accountRows.length === 0 ? (
            <EmptyState title="No accounts yet" body="Add accounts to schedule business reviews." />
          ) : (
            accountRows.map((account) => {
              const qbr = qbrByAccount[account.id];
              const state = qbrState(qbr);
              return (
                <div
                  key={account.id}
                  className="grid gap-2 rounded-md border border-border/60 p-2.5 md:grid-cols-[1fr_10rem_10rem_auto] md:items-end"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{account.name}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge
                        variant={
                          state === "overdue"
                            ? "destructive"
                            : state === "due_soon"
                              ? "secondary"
                              : "outline"
                        }
                      >
                        {QBR_STATE_LABELS[state]}
                      </Badge>
                      {qbr?.last_held_on ? (
                        <Badge variant="outline">Last held {qbr.last_held_on}</Badge>
                      ) : null}
                    </div>
                  </div>
                  <SelectField
                    label="Cadence"
                    value={qbr?.cadence ?? "quarterly"}
                    onChange={(v) => void saveCadence(account.id, v)}
                    options={QBR_CADENCES.map((c) => ({ value: c.value, label: c.label }))}
                  />
                  <div className="grid gap-1.5">
                    <label className="text-xs text-muted-foreground">Next due</label>
                    <Input
                      type="date"
                      value={qbr?.next_due_on ?? ""}
                      onChange={(e) => void setNextDue(account.id, e.target.value)}
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => void markHeld(account.id)}>
                    <CalendarCheck className="size-4" /> Log review
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
